import { Filter } from '@cubejs-client/core';
import { useCubeQuery } from '@cubejs-client/react';
import { DescriptionOutlined } from '@mui/icons-material';
import {
  LinearProgressList,
  Widget,
  WidgetContent,
  WidgetHeader,
  WidgetIcon,
  useScrollIntoView,
} from '@procureai/ui-mui';
import { useTranslation } from 'react-i18next';

import { I18nNamespace } from '~/common/types';
import { FactNegotiationCaseDimension, FactNegotiationCaseMeasure } from '~/common/types/cubes/FactNegotiationCase';
import { CircularProgressCentered } from '~/components/CircularProgressCentered/CircularProgressCentered';
import { useDateRange } from '~/components/DateRangeProvider';
import { convertDateRangeToTimeDimension } from '~/util/cube';

import { MinMaxAverageListItem } from './components/MinMaxAverageListItem';

interface NegotiationProcessWidgetProps {
  filters?: Filter[];
}

const metrics = [
  {
    metric: 'createdToSent',
    pathAvg: FactNegotiationCaseMeasure.AVG_TIME_CREATED_SENT,
    pathMin: FactNegotiationCaseMeasure.MIN_TIME_CREATED_SENT,
    pathMax: FactNegotiationCaseMeasure.MAX_TIME_CREATED_SENT,
  },
  {
    metric: 'sentOpened',
    pathAvg: FactNegotiationCaseMeasure.AVG_TIME_SENT_OPENED,
    pathMin: FactNegotiationCaseMeasure.MIN_TIME_SENT_OPENED,
    pathMax: FactNegotiationCaseMeasure.MAX_TIME_SENT_OPENED,
  },
  {
    metric: 'openedAgreement',
    pathAvg: FactNegotiationCaseMeasure.AVG_TIME_OPENED_AGREEMENT,
    pathMin: FactNegotiationCaseMeasure.MIN_TIME_OPENED_AGREEMENT,
    pathMax: FactNegotiationCaseMeasure.MAX_TIME_OPENED_AGREEMENT,
  },
  {
    metric: 'agreementAwarded',
    pathAvg: FactNegotiationCaseMeasure.AVG_TIME_NEGOTIATED_AWARDED,
    pathMin: FactNegotiationCaseMeasure.MIN_TIME_NEGOTIATED_AWARDED,
    pathMax: FactNegotiationCaseMeasure.MAX_TIME_NEGOTIATED_AWARDED,
  },
];

export const NegotiationProcessWidget = ({ filters }: NegotiationProcessWidgetProps) => {
  const { t } = useTranslation(I18nNamespace.base, { keyPrefix: 'NegotiationProcessWidget' });
  const [widgetRef, active] = useScrollIntoView({ hash: 'negotiation-process' });
  const { currentDateRange } = useDateRange();

  const query = {
    measures: [
      FactNegotiationCaseMeasure.AVG_TIME_CREATED_SENT,
      FactNegotiationCaseMeasure.AVG_TIME_SENT_OPENED,
      FactNegotiationCaseMeasure.AVG_TIME_OPENED_AGREEMENT,
      FactNegotiationCaseMeasure.AVG_TIME_NEGOTIATED_AWARDED,
      FactNegotiationCaseMeasure.MIN_TIME_CREATED_SENT,
      FactNegotiationCaseMeasure.MIN_TIME_SENT_OPENED,
      FactNegotiationCaseMeasure.MIN_TIME_OPENED_AGREEMENT,
      FactNegotiationCaseMeasure.MIN_TIME_NEGOTIATED_AWARDED,
      FactNegotiationCaseMeasure.MAX_TIME_CREATED_SENT,
      FactNegotiationCaseMeasure.MAX_TIME_SENT_OPENED,
      FactNegotiationCaseMeasure.MAX_TIME_OPENED_AGREEMENT,
      FactNegotiationCaseMeasure.MAX_TIME_NEGOTIATED_AWARDED,
    ],
    filters,
  };

  const { resultSet, isLoading } = useCubeQuery({
    ...query,
    timeDimensions: convertDateRangeToTimeDimension(FactNegotiationCaseDimension.CREATED_AT, currentDateRange),
  });

  const values = resultSet?.rawData()?.[0];

  return (
    <Widget ref={widgetRef} active={active}>
      <WidgetHeader
        title={t('title')}
        icon={<WidgetIcon icon={<DescriptionOutlined fontSize="small" />} />}
        subTitle={t('subTitle')}
      />
      <WidgetContent>
        {!isLoading && values ? (
          <LinearProgressList>
            {metrics.map((p) => (
              <MinMaxAverageListItem
                key={p.metric}
                metric={p.metric}
                values={values}
                pathAvg={p.pathAvg}
                pathMin={p.pathMin}
                pathMax={p.pathMax}
              />
            ))}
          </LinearProgressList>
        ) : (
          <CircularProgressCentered sx={{ position: 'relative' }} />
        )}
      </WidgetContent>
    </Widget>
  );
};
